import React, { useState } from 'react';
import {
  Button,
  Alert,
  ListGroup,
  Badge,
  Form,
} from 'react-bootstrap';
import {
  FiletypePdf,
  FileText,
} from 'react-bootstrap-icons';
import { createFromPdf, submitReviewedOcr } from '../../clients/order_client';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo, logError, logWarn } from '../../config_and_helpers/logger';
import {
  buildOrderPdfUploadOutcomes,
  filterErrorsForUploadToasts,
  filterReviewCandidatesForCreatedOrders,
  isDuplicateOrderErrorMessage,
  markOutcomeReviewSuccess,
} from '../../utils/orderPdfUploadOutcomes';
import PdfFileUploadPanel from '../Generic/PdfFileUploadPanel';
import { PROCURE_PDF_UPLOAD_BTN_CLASS } from '../Glass/procureModalUploadButtons';
import OrderOcrReviewStep from './OrderOcrReviewStep';
import OrderPdfUploadOutcomeTable from './OrderPdfUploadOutcomeTable';

const LOG_CONTEXT = 'OrderPdfUpload';

/**
 * Merges OCR handoff arrays from several `order_client.createFromPdf` results (multi-file path).
 * @param {Object[]} results
 * @returns {{ ocrfReviewSummary: Array, ocrfParseSummary: Array, reviewAvailable: boolean, ocrfLegacyFallbackReview: * }}
 */
function mergeOrderOcrFromResults(results) {
  const list = (results || []).filter(Boolean);
  const ocrfReviewSummary = list.flatMap(r => r.ocrfReviewSummary || []);
  const ocrfParseSummary = list.flatMap(r => r.ocrfParseSummary || []);
  const reviewAvailable = list.some(r => r.reviewAvailable);
  const legacyParts = list
    .map(r => r.ocrfLegacyFallbackReview)
    .filter(v => v != null && v !== '');
  const ocrfLegacyFallbackReview =
    legacyParts.length === 0
      ? null
      : legacyParts.length === 1
        ? legacyParts[0]
        : legacyParts;
  return { ocrfReviewSummary, ocrfParseSummary, reviewAvailable, ocrfLegacyFallbackReview };
}

/**
 * Merges multi-file `createFromPdf` results (OCR handoff + created orders + errors).
 * @param {Object[]} results
 * @param {number} totalFileCount
 * @returns {Object}
 */
function mergeOrderUploadResults(results, totalFileCount) {
  const list = (results || []).filter(Boolean);
  const ocr = mergeOrderOcrFromResults(list);
  const createdOrders = list.flatMap(r => r.createdOrders || []);
  const totalCreated = list.reduce(
    (sum, r) => sum + Number(r.totalCreated || 0),
    0
  );
  const errors = list.flatMap(r => r.errors || []);
  const successfulResults = list.filter(r => r.success !== false);
  const uploadFailed = list.some(r => r.success === false);
  const hasOutcomes =
    successfulResults.length > 0 ||
    createdOrders.length > 0 ||
    totalCreated > 0;
  const success = hasOutcomes && !uploadFailed;

  return {
    ...ocr,
    success,
    createdOrders,
    totalCreated,
    errors,
    extractedData: list.flatMap(r => r.extractedData || []),
    totalFilesProcessed: successfulResults.length,
    message:
      createdOrders.length > 0
        ? `${createdOrders.length} of ${totalFileCount} file(s) created order(s).`
        : `Processed ${successfulResults.length} of ${totalFileCount} file(s).`,
    toast: () => {
      const created = createdOrders.length || totalCreated;
      const message = created
        ? `${created} order(s) created from PDF upload.`
        : `PDF processing completed for ${successfulResults.length} of ${totalFileCount} file(s).`;
      showToast(message, created ? 'success' : 'warning', 'top-right', 3000);
    },
  };
}

function getReviewJoinKey(row, fallbackIndex = 0) {
  if (row && row.file_index !== undefined && row.file_index !== null) {
    return `idx:${row.file_index}`;
  }
  if (row && row.pdf_name) {
    return `name:${row.pdf_name}`;
  }
  return `fallback:${fallbackIndex}`;
}

/** @typedef {{ key: string, file_index: *, pdf_name: string, source_mode: string, review_errors: Array, can_submit_reviewed_payload: boolean, ocr_extraction_preview: *, order_lookup_status: string|null, creation_mode: string|null, quote_candidates: Array }} OrderReviewCandidate */

/**
 * Whether this file row should appear in the order OCR review UI (parse summary preferred for join).
 * @param {Object|null} row - `ocrf_review_summary` row
 * @param {Object|null} parse - matching `ocrf_parse_summary` row
 * @param {*} preview - merged `ocr_extraction_preview`
 * @returns {boolean}
 */
function shouldIncludeOrderReviewCandidate(row, parse, preview) {
  if (preview == null) return false;
  const statusRaw = parse?.order_lookup_status ?? row?.order_lookup_status;
  const status = statusRaw != null && statusRaw !== '' ? String(statusRaw).trim() : '';
  if (status && /^(found|not_found|invalid)$/i.test(status)) {
    return true;
  }
  const canSubmit =
    parse?.can_submit_reviewed_payload === true ||
    row?.can_submit_reviewed_payload === true;
  return canSubmit;
}

/**
 * Builds one merged candidate object for the review step (prefer parse row for resolver fields).
 * @param {string} key
 * @param {Object|null} row
 * @param {Object|null} parse
 * @param {number} indexFallback
 * @returns {OrderReviewCandidate|null}
 */
function buildOrderReviewCandidateRow(key, row, parse, indexFallback) {
  const preview =
    row?.ocr_extraction_preview || parse?.ocr_extraction_preview || null;
  if (!shouldIncludeOrderReviewCandidate(row, parse, preview)) {
    return null;
  }
  const qcParse = parse?.quote_candidates;
  const qcRow = row?.quote_candidates;
  const quoteCandidates = Array.isArray(qcParse)
    ? qcParse
    : Array.isArray(qcRow)
      ? qcRow
      : [];
  const reviewErrorsRaw = parse?.review_errors ?? row?.review_errors;
  const review_errors = Array.isArray(reviewErrorsRaw) ? reviewErrorsRaw : [];
  const can_submit_reviewed_payload =
    parse?.can_submit_reviewed_payload === true ||
    row?.can_submit_reviewed_payload === true;

  return {
    key,
    file_index: row?.file_index ?? parse?.file_index ?? indexFallback,
    pdf_name: row?.pdf_name || parse?.pdf_name || '',
    source_mode: parse?.source_mode || row?.source_mode || '',
    review_errors,
    can_submit_reviewed_payload,
    ocr_extraction_preview: preview,
    order_lookup_status:
      parse?.order_lookup_status != null && parse?.order_lookup_status !== ''
        ? String(parse.order_lookup_status)
        : row?.order_lookup_status != null && row?.order_lookup_status !== ''
          ? String(row.order_lookup_status)
          : null,
    creation_mode:
      parse?.creation_mode != null
        ? parse.creation_mode
        : row?.creation_mode != null
          ? row.creation_mode
          : null,
    quote_candidates: quoteCandidates,
  };
}

/**
 * Lists per-file review rows for Order PDF OCR (supports PO-not-found + quote suggestions).
 * @param {Array} ocrReviewSummary
 * @param {Array} ocrParseSummary
 * @returns {OrderReviewCandidate[]}
 */
function buildOrderReviewCandidates(ocrReviewSummary, ocrParseSummary) {
  const parseByKey = new Map();
  (ocrParseSummary || []).forEach((row, i) => {
    parseByKey.set(getReviewJoinKey(row, i), row);
  });
  const out = [];
  const seen = new Set();
  (ocrReviewSummary || []).forEach((row, i) => {
    const key = getReviewJoinKey(row, i);
    seen.add(key);
    const parse = parseByKey.get(key);
    const candidate = buildOrderReviewCandidateRow(key, row, parse, i);
    if (candidate) out.push(candidate);
  });
  (ocrParseSummary || []).forEach((row, i) => {
    const key = getReviewJoinKey(row, i);
    if (seen.has(key)) return;
    const candidate = buildOrderReviewCandidateRow(key, null, row, i);
    if (candidate) out.push(candidate);
  });
  return out.sort((a, b) => Number(a.file_index || 0) - Number(b.file_index || 0));
}

function getOrderUpsertActionText(action) {
  const a = String(action || '').trim();
  if (!a) return 'Order upsert completed from reviewed OCR.';
  return `Order upsert completed (${a}).`;
}

/**
 * User-facing line for known `submit_reviewed_ocr` top-level `error` codes (400 responses).
 * @param {Object} [details] - `error.response.data` from the API
 * @returns {string}
 */
function formatSubmitReviewedOcrError(details) {
  const code = details?.error;
  const map = {
    missing_selected_quote_for_create:
      'Select a quote to create an order for this PO, or correct the PO number if it was wrong.',
    extracted_cats_not_in_quote:
      'Some catalog lines do not match the selected quote. Edit lines or choose another quote.',
    quote_already_has_order:
      'That quote is already linked to an order. Pick another suggestion or reconcile offline.',
    corporate_order_ref_already_used:
      'This PO number is already used by another order. Verify the PO or resolve the conflict offline.',
    selected_quote_not_found:
      'The selected quote is no longer valid. Re-upload or refresh suggestions and try again.',
    strict_po_lookup_failed:
      'PO lookup blocked further processing. See server messages below.',
    invalid_selected_quote_id:
      'Invalid quote id. Pick a suggestion from the list again.',
  };
  if (code && map[code]) return map[code];
  return '';
}

/**
 * Order PDF upload with OCR parse/review and `submit_reviewed_ocr` upsert.
 *
 * @param {Object} props
 * @param {function} props.onPdfProcessed - Called after successful reviewed submit or legacy create
 * @param {boolean} [props.disabled]
 * @param {string} props.token - Auth token for order_client calls
 * @param {string} [props.defaultReceivedBy] - Prefill display name for `received_by` on review submit
 */
const PdfUploadComponent = ({
  onPdfProcessed,
  disabled = false,
  token,
  defaultReceivedBy = '',
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  /** When true, orders created via create_from_pdf omit finance daily digest. */
  const [excludeFromFinanceDigest, setExcludeFromFinanceDigest] =
    useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // 'success', 'error', null
  const [errorMessage, setErrorMessage] = useState('');
  const [extractedData, setExtractedData] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [reviewAvailable, setReviewAvailable] = useState(false);
  const [ocrfLegacyFallbackReview, setOcrfLegacyFallbackReview] = useState(null);
  const [reviewCandidates, setReviewCandidates] = useState([]);
  const [activeReviewKey, setActiveReviewKey] = useState('');
  const [reviewSubmitErrorByKey, setReviewSubmitErrorByKey] = useState({});
  const [reviewSubmitFailureDetailsByKey, setReviewSubmitFailureDetailsByKey] = useState({});
  const [reviewSubmitSuccessByKey, setReviewSubmitSuccessByKey] = useState({});
  const [isSubmittingReviewed, setIsSubmittingReviewed] = useState(false);
  /** Per-file chosen quote PK for `not_found` create path (`submit_reviewed_ocr`). */
  const [selectedQuoteIdByKey, setSelectedQuoteIdByKey] = useState({});
  /** Per-file create/fail rows after upload (and updated after OCR review submit). */
  const [uploadOutcomes, setUploadOutcomes] = useState([]);
  /** Maps `createFromPdf` OCR handoff fields to local state. */
  const setOcrFromHandoff = r => {
    if (!r) {
      setReviewAvailable(false);
      setOcrfLegacyFallbackReview(null);
      setReviewCandidates([]);
      setActiveReviewKey('');
      setReviewSubmitErrorByKey({});
      setReviewSubmitFailureDetailsByKey({});
      setReviewSubmitSuccessByKey({});
      setSelectedQuoteIdByKey({});
      setUploadOutcomes([]);
      setIsSubmittingReviewed(false);
      return;
    }
    const nextReview = r.ocrfReviewSummary || [];
    const nextParse = r.ocrfParseSummary || [];
    setReviewAvailable(Boolean(r.reviewAvailable));
    setOcrfLegacyFallbackReview(r.ocrfLegacyFallbackReview ?? null);
    const candidates = buildOrderReviewCandidates(nextReview, nextParse);
    setReviewCandidates(candidates);
    setSelectedQuoteIdByKey({});
    setReviewSubmitFailureDetailsByKey({});
    if (candidates.length) {
      logInfo(LOG_CONTEXT, 'buildOrderReviewCandidates', {
        count: candidates.length,
        withQuotePath: candidates.filter(
          c =>
            String(c.order_lookup_status || '').toLowerCase() === 'not_found' &&
            c.creation_mode === 'suggest_quotes' &&
            Array.isArray(c.quote_candidates) &&
            c.quote_candidates.length > 0
        ).length,
      });
      setActiveReviewKey(prev =>
        candidates.some(c => c.key === prev) ? prev : candidates[0].key
      );
    } else {
      setActiveReviewKey('');
    }
  };

  const resetFileSelectionState = () => {
    setUploadStatus(null);
    setErrorMessage('');
    setExtractedData(null);
    setUploadProgress(0);
    setCurrentFileIndex(0);
    setOcrFromHandoff(null);
  };

  const handleSelectedFilesChange = files => {
    setSelectedFiles(files);
    resetFileSelectionState();
  };

  const handleFileValidationError = message => {
    if (message) {
      setErrorMessage(message);
      setUploadStatus('error');
    } else {
      setErrorMessage('');
    }
  };

  const getFilteredUploadErrors = (errors, createdOrders, files) =>
    filterErrorsForUploadToasts({
      errors: errors || [],
      createdOrders: createdOrders || [],
      selectedFiles: files || [],
    });

  const uploadFilesIndividually = async files => {
    const results = [];
    const totalFiles = files.length;

    for (let i = 0; i < files.length; i++) {
      const currentFile = files[i];
      const fileIndex = i + 1;

      setCurrentFileIndex(fileIndex);
      setUploadProgress((fileIndex / totalFiles) * 100);

      try {
        // Process each file individually
        const result = await createFromPdf(token, [currentFile], {
          excludeFromFinanceDigest,
        });
        results.push(result);
      } catch (error) {
        results.push({
          success: false,
          error: error.message,
          filename: currentFile.name,
        });
      }
    }

    return results;
  };

  /**
   * Builds outcome table, applies OCR handoff state, and completes upload when no review is pending.
   * @param {Object} result - Merged `createFromPdf` result
   * @param {File[]} filesSnapshot - Files uploaded in this batch
   */
  const applyUploadResult = (result, filesSnapshot) => {
    const nextReview = result.ocrfReviewSummary || [];
    const nextParse = result.ocrfParseSummary || [];
    const createdOrders = result.createdOrders || [];
    const candidates = filterReviewCandidatesForCreatedOrders(
      buildOrderReviewCandidates(nextReview, nextParse),
      createdOrders
    );
    const outcomes = buildOrderPdfUploadOutcomes({
      selectedFiles: filesSnapshot,
      createdOrders,
      errors: result.errors,
      ocrParseSummary: nextParse,
    });

    setReviewAvailable(Boolean(result.reviewAvailable));
    setOcrfLegacyFallbackReview(result.ocrfLegacyFallbackReview ?? null);
    setReviewCandidates(candidates);
    setSelectedQuoteIdByKey({});
    setReviewSubmitFailureDetailsByKey({});
    setReviewSubmitSuccessByKey({});
    setUploadOutcomes(outcomes);

    if (candidates.length) {
      setActiveReviewKey(prev =>
        candidates.some(c => c.key === prev) ? prev : candidates[0].key
      );
    } else {
      setActiveReviewKey('');
    }

    const needsReview = candidates.length > 0;
    const directOrdersCreated =
      createdOrders.length > 0 || Number(result.totalCreated || 0) > 0;

    const uploadSucceeded = result.success !== false;

    logInfo(LOG_CONTEXT, 'applyUploadResult', {
      outcomesCount: outcomes.length,
      createdOrdersCount: createdOrders.length,
      needsReview,
      directOrdersCreated,
      uploadSucceeded,
    });

    if (
      directOrdersCreated &&
      !needsReview &&
      uploadSucceeded &&
      onPdfProcessed
    ) {
      onPdfProcessed({
        success: true,
        totalCreated:
          Number(result.totalCreated || 0) || createdOrders.length,
        createdOrders,
        directUploadComplete: true,
      });
    }

    return {
      candidates,
      needsReview,
      directOrdersCreated,
      uploadSucceeded,
      outcomes,
    };
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setErrorMessage('Please select at least one PDF file first.');
      setUploadStatus('error');
      return;
    }

    setIsUploading(true);
    setUploadStatus(null);
    setErrorMessage('');
    setExtractedData(null);
    setUploadProgress(0);
    setCurrentFileIndex(0);
    setOcrFromHandoff(null);

    const filesSnapshot = [...selectedFiles];

    try {
      if (!token) {
        throw new Error('Authentication token is required');
      }

      let result;

      // Process files individually to avoid 413 errors
      if (selectedFiles.length > 1) {
        const individualResults = await uploadFilesIndividually(selectedFiles);

        result = mergeOrderUploadResults(
          individualResults,
          filesSnapshot.length
        );
      } else {
        result = await createFromPdf(token, selectedFiles, {
          excludeFromFinanceDigest,
        });
      }

      const { needsReview, directOrdersCreated, uploadSucceeded } =
        applyUploadResult(result, filesSnapshot);

      const partialUploadFailure =
        directOrdersCreated && !uploadSucceeded && !needsReview;

      const worked =
        uploadSucceeded &&
        (directOrdersCreated ||
          needsReview ||
          (result.ocrfParseSummary || []).length > 0);

      if (partialUploadFailure) {
        const partialMessage =
          'Order(s) were created but the PO file could not be uploaded. Re-attach the PDF in the order editor.';
        logWarn(LOG_CONTEXT, 'partial PO upload failure after order create', {
          createdOrdersCount: (result.createdOrders || []).length,
          totalCreated: result.totalCreated ?? null,
        });
        setUploadStatus('error');
        setExtractedData(result.extractedData);
        setUploadProgress(100);
        setErrorMessage(partialMessage);
        showToast(
          'Order created but PO file upload failed — re-attach the PDF in the order editor.',
          'warning',
          'top-right',
          5000
        );
        const partialToastableErrors = getFilteredUploadErrors(
          result.errors,
          result.createdOrders,
          filesSnapshot,
        );
        if (partialToastableErrors.length > 0) {
          const filename =
            filesSnapshot.length === 1 ? filesSnapshot[0].name : undefined;
          showErrorToasts(partialToastableErrors, filename);
        }
      } else if (worked) {
        setUploadStatus('success');
        setExtractedData(result.extractedData);
        setUploadProgress(100);

        if (result.errors && result.errors.length > 0) {
          const filename =
            filesSnapshot.length === 1 ? filesSnapshot[0].name : undefined;
          const toastableErrors = getFilteredUploadErrors(
            result.errors,
            result.createdOrders,
            filesSnapshot,
          );
          if (toastableErrors.length > 0) {
            showErrorToasts(toastableErrors, filename);
            if (!directOrdersCreated || needsReview) {
              setErrorMessage(toastableErrors.join('\n'));
            }
          }
        }

        if (result.toast) {
          result.toast();
        }

        if (directOrdersCreated && !needsReview) {
          setSelectedFiles([]);
        }
      } else {
        applyUploadResult(result, filesSnapshot);
        setUploadStatus('error');
        const toastableErrors = getFilteredUploadErrors(
          result.errors,
          result.createdOrders,
          filesSnapshot,
        );
        const errorText =
          toastableErrors.length > 0
            ? toastableErrors.join('\n')
            : result.message || 'Failed to process PDFs';
        setErrorMessage(errorText);

        if (toastableErrors.length > 0) {
          const filename =
            filesSnapshot.length === 1 ? filesSnapshot[0].name : undefined;
          showErrorToasts(toastableErrors, filename);
        }
      }
    } catch (error) {
      setUploadStatus('error');

      // Handle specific 413 error
      if (error.response && error.response.status === 413) {
        setErrorMessage(
          'File upload failed: Total file size too large. Please try uploading fewer files at once or contact your administrator to increase the upload limit.'
        );
      } else if (error.response && error.response.data) {
        // Check for detailed errors in response (e.g., validation errors from backend)
        const errorData = error.response.data;
        const errorText =
          errorData.errors &&
          Array.isArray(errorData.errors) &&
          errorData.errors.length > 0
            ? errorData.errors.join('\n')
            : errorData.message ||
              error.message ||
              'Failed to upload PDFs. Please try again.';
        setErrorMessage(errorText);
      } else {
        setErrorMessage(
          error.message || 'Failed to upload PDFs. Please try again.'
        );
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setCurrentFileIndex(0);
    }
  };

  /**
   * Categorizes error messages to identify specific error types.
   * @param {string|Array} error - Error message or array of error messages
   * @returns {Array} Array of error categories found
   */
  const categorizeErrors = error => {
    const categories = new Set();
    const errorStr = typeof error === 'string' ? error : String(error);
    const errorLower = errorStr.toLowerCase();

    // Missing Supplier ID
    if (
      errorLower.includes('no supplier id found') ||
      errorLower.includes('supplier id not found') ||
      (errorLower.includes('supplier id') &&
        errorLower.includes('not found') &&
        !errorLower.includes('database'))
    ) {
      categories.add('missing_supplier_id');
    }

    // Supplier Not Found (in database)
    if (
      (errorLower.includes('supplier with id') &&
        errorLower.includes('not found')) ||
      (errorLower.includes('supplier') &&
        errorLower.includes('not found') &&
        errorLower.includes('database'))
    ) {
      categories.add('supplier_not_found');
    }

    // Missing Order Number
    if (
      errorLower.includes('no order number') ||
      errorLower.includes('order number not found') ||
      (errorLower.includes('order number') &&
        errorLower.includes('not found') &&
        !errorLower.includes('duplicate'))
    ) {
      categories.add('missing_order_number');
    }

    // Missing Corporate Demand Ref
    if (
      errorLower.includes('no corporate demand') ||
      errorLower.includes('corporate demand ref not found') ||
      (errorLower.includes('corporate demand') &&
        errorLower.includes('not found'))
    ) {
      categories.add('missing_corporate_demand');
    }

    // Missing Requirement Numbers
    if (
      errorLower.includes('no requirement') ||
      errorLower.includes('requirement not found') ||
      (errorLower.includes('requirement') &&
        errorLower.includes('not found') &&
        !errorLower.includes('duplicate'))
    ) {
      categories.add('missing_requirement');
    }

    // Duplicate Order (aligned with filterErrorsForUploadToasts / isDuplicateOrderErrorMessage)
    if (isDuplicateOrderErrorMessage(errorStr)) {
      categories.add('duplicate_order');
    }

    // PDF Parsing Error
    if (
      errorLower.includes('pdf parsing') ||
      errorLower.includes('failed to parse') ||
      errorLower.includes('unable to extract') ||
      errorLower.includes('extraction failed')
    ) {
      categories.add('pdf_parsing_error');
    }

    // No Valid Data
    if (
      errorLower.includes('no valid data') ||
      errorLower.includes('no data extracted') ||
      errorLower.includes('all data failed')
    ) {
      categories.add('no_valid_data');
    }

    return Array.from(categories);
  };

  /**
   * Shows toast notifications for each error category found in the errors.
   * @param {string|Array} errors - Error message or array of error messages
   * @param {string} filename - Optional filename for context
   */
  const showErrorToasts = (errors, filename = '') => {
    if (!errors) return;

    // Convert to array if string
    const errorArray = Array.isArray(errors) ? errors : [errors];
    const allCategories = new Set();

    // Collect all error categories
    errorArray.forEach(error => {
      const categories = categorizeErrors(error);
      categories.forEach(cat => allCategories.add(cat));
    });

    // Show toast for each unique error category
    allCategories.forEach(category => {
      let message = '';
      let type = 'error';

      switch (category) {
        case 'missing_supplier_id':
          message =
            filename && filename !== 'Unknown file'
              ? `${filename}: No supplier ID found in PDF. Please ensure the PDF contains supplier information.`
              : 'No supplier ID found in PDF. Please ensure the PDF contains supplier information.';
          break;

        case 'supplier_not_found':
          message =
            filename && filename !== 'Unknown file'
              ? `${filename}: Supplier ID found but not matched in database. Please set the supplier's Inner Supplier ID in the supplier management page to match the PDF supplier ID.`
              : "Supplier ID found but not matched in database. Please set the supplier's Inner Supplier ID in the supplier management page to match the PDF supplier ID.";
          break;

        case 'missing_order_number':
          message =
            filename && filename !== 'Unknown file'
              ? `${filename}: No order number found in PDF. Please ensure the PDF contains order number information.`
              : 'No order number found in PDF. Please ensure the PDF contains order number information.';
          break;

        case 'missing_corporate_demand':
          message =
            filename && filename !== 'Unknown file'
              ? `${filename}: No corporate demand reference found in PDF. Please ensure the PDF contains corporate demand reference information.`
              : 'No corporate demand reference found in PDF. Please ensure the PDF contains corporate demand reference information.';
          break;

        case 'missing_requirement':
          message =
            filename && filename !== 'Unknown file'
              ? `${filename}: No requirement numbers found in PDF. Please ensure the PDF contains requirement number information.`
              : 'No requirement numbers found in PDF. Please ensure the PDF contains requirement number information.';
          break;

        case 'duplicate_order':
          message =
            filename && filename !== 'Unknown file'
              ? `${filename}: An order with this order number already exists. Please check for duplicates.`
              : 'An order with this order number already exists. Please check for duplicates.';
          type = 'warning';
          break;

        case 'pdf_parsing_error':
          message =
            filename && filename !== 'Unknown file'
              ? `${filename}: Failed to parse PDF or extract data. Please verify the PDF format is correct.`
              : 'Failed to parse PDF or extract data. Please verify the PDF format is correct.';
          break;

        case 'no_valid_data':
          message =
            filename && filename !== 'Unknown file'
              ? `${filename}: No valid data extracted from PDF. The PDF may be corrupted or in an unsupported format.`
              : 'No valid data extracted from PDF. The PDF may be corrupted or in an unsupported format.';
          break;

        default:
          return; // Skip unknown categories
      }

      showToast(message, type, 'top-right', 5000);
    });
  };

  const activeReviewCandidate =
    reviewCandidates.find(c => c.key === activeReviewKey) || null;

  const handleQuoteSelectionChange = quoteId => {
    if (!activeReviewCandidate) return;
    setSelectedQuoteIdByKey(prev => ({
      ...prev,
      [activeReviewCandidate.key]: quoteId,
    }));
    logInfo(LOG_CONTEXT, 'Order OCR quote candidate selected', {
      reviewKey: activeReviewCandidate.key,
      quoteId,
    });
  };

  const handleSubmitReviewed = async payload => {
    if (!activeReviewCandidate || !token) return;
    const k = activeReviewCandidate.key;
    logInfo(LOG_CONTEXT, 'submitReviewedOcr starting', {
      reviewKey: k,
      orderLookupStatus: activeReviewCandidate.order_lookup_status ?? null,
      creationMode: activeReviewCandidate.creation_mode ?? null,
      selectedQuoteId: payload?.selected_quote_id ?? null,
      itemCount: Array.isArray(payload?.ocr_extraction_preview?.items)
        ? payload.ocr_extraction_preview.items.length
        : 0,
    });
    setIsSubmittingReviewed(true);
    setReviewSubmitErrorByKey(prev => ({ ...prev, [k]: '' }));
    setReviewSubmitFailureDetailsByKey(prev => ({ ...prev, [k]: null }));
    try {
      const result = await submitReviewedOcr(token, payload);
      if (!result.success) {
        const details = result.details || {};
        const friendly = formatSubmitReviewedOcrError(details);
        const primary =
          friendly ||
          result.message ||
          (typeof result.error === 'string' ? result.error : '') ||
          'Failed to submit reviewed OCR payload.';
        setReviewSubmitErrorByKey(prev => ({ ...prev, [k]: primary }));
        setReviewSubmitFailureDetailsByKey(prev => ({
          ...prev,
          [k]: {
            review_errors: Array.isArray(details.review_errors)
              ? details.review_errors
              : [],
            post_extraction_review: details.post_extraction_review,
            apiError: details.error,
          },
        }));
        logInfo(LOG_CONTEXT, 'submitReviewedOcr rejected', {
          reviewKey: k,
          apiError: details.error,
        });
        return;
      }
      const nextSuccessByKey = {
        ...reviewSubmitSuccessByKey,
        [k]: result,
      };
      const nextOutcomes = markOutcomeReviewSuccess(
        uploadOutcomes,
        activeReviewCandidate.pdf_name,
        result.orderId
      );
      setReviewSubmitSuccessByKey(nextSuccessByKey);
      setReviewSubmitFailureDetailsByKey(prev => ({ ...prev, [k]: null }));
      setUploadOutcomes(nextOutcomes);
      logInfo(LOG_CONTEXT, 'submitReviewedOcr succeeded', {
        reviewKey: k,
        orderId: result.orderId,
        orderUpsertAction: result.orderUpsertAction,
      });
      showToast(
        getOrderUpsertActionText(result.orderUpsertAction),
        'success',
        'top-right',
        3000
      );

      const allReviewSubmitted = reviewCandidates.every(
        c => nextSuccessByKey[c.key]
      );
      if (allReviewSubmitted && onPdfProcessed) {
        onPdfProcessed({
          success: true,
          totalCreated: nextOutcomes.filter(r => r.success).length,
          reviewedSubmit: true,
          reviewedSubmitResult: result,
        });
      }
    } catch (error) {
      logError(LOG_CONTEXT, 'submitReviewedOcr threw', {
        reviewKey: k,
        message: error?.message,
      });
      setReviewSubmitErrorByKey(prev => ({
        ...prev,
        [k]: error?.message || 'Failed to submit reviewed OCR payload.',
      }));
    } finally {
      setIsSubmittingReviewed(false);
    }
  };

  const uploadSuccessMessage =
    uploadStatus === 'success' && !errorMessage
      ? uploadOutcomes.some(r => r.success)
        ? 'PDF upload finished. See results below.'
        : reviewCandidates.length > 0
          ? 'PDF parsed. Review extracted data below before submitting.'
          : 'PDF processing completed.'
      : '';

  return (
    <PdfFileUploadPanel
      title="PDF Upload & Processing"
      selectedFiles={selectedFiles}
      onSelectedFilesChange={handleSelectedFilesChange}
      onValidationError={handleFileValidationError}
      onProcess={handleUpload}
      isProcessing={isUploading}
      disabled={disabled}
      errorMessage={errorMessage}
      successMessage={uploadSuccessMessage}
      uploadProgress={isUploading ? uploadProgress : null}
      currentFileIndex={isUploading ? currentFileIndex : null}
      totalFileCount={isUploading ? selectedFiles.length : null}
      helperText="Maximum file size: 10MB per file. Only PDF files are accepted. You can select multiple files. Files will be processed individually to avoid upload limits."
      processButtonClassName={PROCURE_PDF_UPLOAD_BTN_CLASS}
    >
        <Form.Group
          controlId="pdfExcludeFromFinanceDigest"
          className="mb-3"
        >
          <Form.Check
            type="checkbox"
            name="excludeFromFinanceDigest"
            label="Exclude from finance digest"
            checked={excludeFromFinanceDigest}
            disabled={disabled || isUploading}
            onChange={e => {
              const checked = e.target.checked;
              setExcludeFromFinanceDigest(checked);
              logInfo(LOG_CONTEXT, 'excludeFromFinanceDigest toggled', {
                excludeFromFinanceDigest: checked,
              });
            }}
          />
        </Form.Group>

        {reviewCandidates.length > 0 && (
          <div className="mt-3 p-2 border rounded bg-white">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <h6 className="mb-0">OCR review/edit required</h6>
              <Badge bg="warning" text="dark">
                {reviewCandidates.length} reviewable file(s)
              </Badge>
            </div>
            <div className="d-flex flex-wrap gap-2 mb-2">
              {reviewCandidates.map(candidate => {
                const isActive = candidate.key === activeReviewKey;
                const submitted = reviewSubmitSuccessByKey[candidate.key];
                return (
                  <Button
                    key={`review-candidate-${candidate.key}`}
                    size="sm"
                    variant={isActive ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveReviewKey(candidate.key)}
                    disabled={isSubmittingReviewed}
                  >
                    {candidate.pdf_name || `File ${candidate.file_index ?? '—'}`}
                    {submitted ? ' (submitted)' : ''}
                  </Button>
                );
              })}
            </div>

            {activeReviewCandidate && (
              <OrderOcrReviewStep
                key={activeReviewCandidate.key}
                reviewRow={activeReviewCandidate}
                defaultReceivedBy={defaultReceivedBy}
                selectedQuoteId={selectedQuoteIdByKey[activeReviewCandidate.key] ?? null}
                onQuoteSelectionChange={handleQuoteSelectionChange}
                isSubmitting={isSubmittingReviewed}
                submitError={reviewSubmitErrorByKey[activeReviewCandidate.key] || ''}
                submitFailureDetails={
                  reviewSubmitFailureDetailsByKey[activeReviewCandidate.key] || null
                }
                onBack={() => setActiveReviewKey('')}
                onSubmit={handleSubmitReviewed}
              />
            )}
          </div>
        )}

        {uploadOutcomes.length > 0 && (
          <OrderPdfUploadOutcomeTable outcomes={uploadOutcomes} />
        )}

        {reviewAvailable && ocrfLegacyFallbackReview != null && (
          <Alert variant="warning" className="mt-3 mb-0 py-2 small">
            <strong>Manual review</strong> — the server returned normalized content for
            support because the order could not be created automatically. Expand the
            payload below or contact an administrator.
          </Alert>
        )}

        {ocrfLegacyFallbackReview != null && (
          <details className="mt-2 small">
            <summary className="text-muted">Legacy fallback review payload (ocrf_legacy_fallback_review)</summary>
            <pre
              className="mt-1 mb-0 p-2 bg-light border rounded small overflow-auto"
              style={{ maxHeight: 200 }}
            >
              {JSON.stringify(ocrfLegacyFallbackReview, null, 2)}
            </pre>
          </details>
        )}

        {/* Extracted Data Display */}
        {extractedData && extractedData.length > 0 && (
          <div className="extracted-data-container mt-3">
            <h6 className="d-flex align-items-center gap-2 mb-2">
              <FileText size={16} className="text-success" />
              Extracted Data
            </h6>
            <ListGroup>
              {extractedData.map((fileData, index) => (
                <ListGroup.Item key={index} className="border-0 bg-white">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <FiletypePdf size={14} className="text-danger" />
                    <strong className="small">{fileData.filename}</strong>
                  </div>
                  <div className="ms-4">
                    <div className="mb-1">
                      <Badge bg="primary" className="me-2">
                        Order Number
                      </Badge>
                      <span className="small">{fileData.order_number}</span>
                    </div>
                    <div className="mb-1">
                      <Badge bg="info" className="me-2">
                        Corporate Demand Ref
                      </Badge>
                      <span className="small">
                        {fileData.corporate_demand_ref}
                      </span>
                    </div>
                    <div>
                      <Badge bg="secondary" className="me-2">
                        All Requirements
                      </Badge>
                      <span className="small">
                        {fileData.all_requirement_numbers.join(', ')}
                      </span>
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        )}
    </PdfFileUploadPanel>
  );
};

export default PdfUploadComponent;
